import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';
import Icon from '@app/components/common/Icon';
import Modal from '@app/components/common/Modal';
import Table from '@app/components/common/Table';
import Typography from '@app/components/common/Typography';
import LoadingPDFCard from '@app/components/LoggedIn/Reports/LoadingPDFCard';
import { dateTimeToSettingsTimeZone } from '@app/helpers/time/time';
import useRootSelector from '@app/hooks/useRootSelector';
import { fetchReportPDFWeb } from '@app/state/reports/reportsWebActions';
import { useAppDispatch } from '@app/state/store';
import { displayFlexRowCenterStyles } from '@app/theme/commonStyles';
import { createColumnHelper, SortDirection } from '@tanstack/react-table';
import {
  EReportPeriod,
  formatMoney,
  getEndOfShiftReportInitialState,
  IEndOfShiftReport,
  TEditEmployeeShift,
  TEmployeeDetailsScreenShift,
} from '@westondev/tableturn-core';
import { useContext, useEffect, useMemo, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import { EmployeesScreenContext } from '../../EmployeesContext';
import LoadingMore from '../../EmployeesTable/LoadingMore';
import AddEditShiftModal from './AddEditShiftModal';

type IShiftDetailsRow = TEmployeeDetailsScreenShift & {
  id: number;
  edit: string;
  report: string;
};

const columnHelper = createColumnHelper<IShiftDetailsRow>();

const ShiftDetails = ({ t }: WithTranslation) => {
  const dispatch = useAppDispatch();

  const {
    users,
    hasFetched,
    isFetching,
    setShiftsSorting,
    shiftsSorting,
    shifts,
    setShift,
    user,
    shift,
    fetchShifts,
  } = useContext(EmployeesScreenContext);

  const pdfUrl = useRootSelector(state => state.reports.changeData.reportUrl);

  const [reportData, setReportData] = useState<IEndOfShiftReport | null>(null);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const shiftsFormatted = useMemo(
    () =>
      shifts.map(_shift => ({
        ..._shift,
        id: _shift.shiftId,
        edit: ' ',
        report: ' ',
      })),
    [shifts],
  );

  const handleOnSort = (sort: {
    sortDirection: SortDirection | false;
    columnId: string;
  }) => {
    if (sort.sortDirection === false) {
      setShiftsSorting({
        field: null,
        direction: null,
      });
      return;
    }
    const orderDirection = sort.sortDirection ? sort.sortDirection : null;

    setShiftsSorting({
      field: sort.columnId as keyof TEmployeeDetailsScreenShift,
      direction: orderDirection,
    });
  };

  const COLUMN_VALUES = useMemo(
    () => [
      columnHelper.accessor('edit', {
        header: ' ',
        cell: info => (
          <Box
            csx={[displayFlexRowCenterStyles, { cursor: 'pointer' }]}
            onClick={() =>
              setShift({
                shiftId: info.row.original.shiftId,
                endShift: info.row.original.shiftEnd,
                startShift: info.row.original.shiftStart,
                organizationUserId: user?.orgUserId || 0,
                roleId: info.row.original.roleId,
              } as TEditEmployeeShift)
            }>
            <Icon name="MdEdit" size="20px" />
          </Box>
        ),
        size: 100,
        enableSorting: false,
      }),
      columnHelper.accessor('shiftStart', {
        header: t(
          'managementModule.employeesScreen.employeeDetailsTable.shift',
        ),
        cell: info => (
          <Box>
            <Typography>
              {dateTimeToSettingsTimeZone(info.getValue()).toFormat(
                'MM/dd/yy hh:mm a',
              )}
              {info.row.original.shiftEnd && ' -'}
            </Typography>
            {info.row.original.shiftEnd && (
              <Typography>
                {dateTimeToSettingsTimeZone(
                  info.row.original.shiftEnd,
                ).toFormat('MM/dd/yy hh:mm a')}
              </Typography>
            )}
          </Box>
        ),
        size: 160,
      }),
      columnHelper.accessor('role', {
        header: t('managementModule.employeesScreen.employeesTable.role'),
        cell: info => <Typography>{info.getValue()}</Typography>,
        minSize: 110,
        enableSorting: false,
      }),
      columnHelper.accessor('hours', {
        header: t('managementModule.employeesScreen.employeesTable.hours'),
        cell: info => <Typography>{info.getValue().toFixed(2)}</Typography>,
        minSize: 120,
        enableSorting: false,
      }),
      columnHelper.accessor('sales', {
        header: t('managementModule.employeesScreen.employeesTable.sales'),
        cell: info => (
          <Typography>{formatMoney(null, info.getValue())}</Typography>
        ),
        minSize: 150,
        enableSorting: false,
      }),
      columnHelper.accessor('tipsEarned', {
        header: t('managementModule.employeesScreen.employeesTable.tipsEarned'),
        cell: info => (
          <Typography>{formatMoney(null, info.getValue())}</Typography>
        ),
        minSize: 150,
        enableSorting: false,
      }),
      columnHelper.accessor('report', {
        header: ' ',
        cell: info => (
          <Box>
            <Button
              csx={{ height: 40 }}
              onClick={() => {
                const newReport = getEndOfShiftReportInitialState();
                newReport.ownerIds = [user?.orgUserId || 0];
                newReport.period = EReportPeriod.CUSTOM;
                const reportStartTime = info.row.original.shiftStart;
                const reportEndTime =
                  info.row.original.shiftEnd || new Date().toISOString();

                newReport.startDate = reportStartTime;
                newReport.endDate = reportEndTime;

                newReport.name = `${user?.name}'s Shift Report`;

                dispatch(fetchReportPDFWeb(newReport, 'endOfShiftReports'));

                setReportData(newReport);
              }}>
              {t(
                'managementModule.employeesScreen.employeeDetailsTable.report',
              )}
            </Button>
          </Box>
        ),
        enableSorting: false,
        size: 110,
      }),
    ],
    [t, setShift, user?.orgUserId, user?.name, dispatch],
  );

  return (
    <>
      <AddEditShiftModal
        active={shift !== null}
        isAdd={shift?.shiftId === 0}
        name={user?.name || ''}
        onCancel={() => setShift(null)}
      />
      <Modal
        size="80%"
        height="80%"
        isActive={!!reportData}
        onModalClose={() => setReportData(null)}>
        {!!pdfUrl && (
          <object
            data={`${pdfUrl}#zoom=150`}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{
              flex: 1,
            }}>
            <LoadingPDFCard />
          </object>
        )}
      </Modal>
      <Box
        csx={{
          height: '100%',
          position: 'relative',
        }}>
        {hasFetched && (
          <Table
            scrollEnabled
            nestedScrollEnabled
            alignHeaders={{
              hours: 'right',
              sales: 'right',
              tipsEarned: 'right',
            }}
            align={{
              hours: 'right',
              sales: 'right',
              tipsEarned: 'right',
              report: 'center',
            }}
            columns={COLUMN_VALUES}
            data={users ? shiftsFormatted : []}
            persistentScrollbar
            listEmptyText={
              <Typography align="center">{t('commonTexts.noData')}</Typography>
            }
            manualSorting
            enableAlphabeticalSorting
            onEndReached={() => {
              fetchShifts(true);
            }}
            onSortPress={handleOnSort}
            alwaysShowSortIcon
            columnSorting={
              shiftsSorting.field
                ? [
                    {
                      id: shiftsSorting.field,
                      desc: shiftsSorting.direction === 'desc',
                    },
                  ]
                : []
            }
          />
        )}
        {isFetching && hasFetched && (
          <LoadingMore>
            <Typography>
              {t('managementModule.employeesScreen.employeesTable.loading')}
            </Typography>
          </LoadingMore>
        )}
      </Box>
    </>
  );
};

export default ShiftDetails;
